import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";

const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const sgn = (v) => `${v < 0 ? "-" : "+"}${usd(Math.abs(v || 0))}`;
const tone = (v) => (v == null ? "text-cyan-200/60" : v >= 0 ? "text-[#2CF6B3]" : "text-[#FF3D68]");
const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }) : "--");
const STAGES = ["quarter", "half", "full"];

export const useEngineV2 = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let on = true;
    const load = () => api.get("/engine/v2").then((r) => on && setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { on = false; clearInterval(t); };
  }, []);
  return d;
};

const Stat = ({ label, value, cls = "text-cyan-50", testid }) => (
  <div className="fc-panel px-[0.7em] py-[0.4em]" data-testid={testid}>
    <p className="fc-label">{label}</p>
    <p className={`nx-orbitron text-[1.7em] font-black leading-tight ${cls}`}>{value}</p>
  </div>
);

const StageBar = ({ st }) => (
  <div className="fc-panel px-[0.7em] py-[0.45em]" data-testid="v2-stage">
    <p className="fc-label">SIZE STAGE · {st?.next}</p>
    <div className="flex gap-[0.4em] mt-[0.3em]">
      {STAGES.map((s) => (
        <span key={s} className={`flex-1 text-center fc-num font-bold rounded-full py-[0.15em] border ${st?.stage === s ? "bg-[#00F0FF] text-[#030712] border-[#00F0FF]" : "border-cyan-300/25 text-cyan-200/50"}`}
          data-testid={`v2-stage-${s}`}>{s.toUpperCase()}</span>
      ))}
    </div>
  </div>
);

const TM_CLS = { PASS: "text-[#2CF6B3]", BLOCK: "text-[#FF3D68]", ADVISORY: "text-amber-200" };
const TmBadge = ({ tm, t }) => (
  <span className={`truncate ${tm ? TM_CLS[tm.verdict] || "text-cyan-200/50" : "text-cyan-200/30"}`} data-testid={`v2-tm-${t}`}>
    {!tm ? "--" : tm.loading ? "TM …" : `TM ${tm.verdict === "ADVISORY" ? "ADV" : tm.verdict} ${tm.n ? `${Math.round(tm.wr)}% ${tm.exp > 0 ? "+" : ""}${tm.exp?.toFixed(2)}` : ""}`}
  </span>
);

const Scan = ({ rows }) => (
  <section className="fc-panel min-h-0 p-[0.6em] flex flex-col" data-testid="v2-scan">
    <p className="fc-label mb-[0.3em]">LIVE SCAN · early movers ({rows.length})</p>
    <div className="min-h-0 flex-1 overflow-hidden">
      {rows.slice(0, 16).map((c) => (
        <div key={c.ticker} className="grid items-baseline fc-num text-[1em] py-[0.12em] border-b border-white/5 gap-[0.5em]" style={{ gridTemplateColumns: "6em 4.4em 1fr 5.5em 12em" }} data-testid={`v2-scan-${c.ticker}`}>
          <b className="text-cyan-50 truncate">{c.ticker}</b>
          <span className={c.direction === "long" ? "text-[#2CF6B3]" : "text-[#FF3D68]"}>{c.direction.toUpperCase()}</span>
          <span className="text-cyan-200/75 truncate">{c.invo?.length ? <b className="text-amber-300 mr-[0.4em]" data-testid={`v2-invo-${c.ticker}`}>INVO</b> : null}{c.trigger}</span>
          <span className={c.blocked_by?.length ? "text-[#FF3D68]" : c.holding ? "text-[#2CF6B3]" : "text-amber-200"}>{c.blocked_by?.length ? "BLOCKED" : c.holding ? "READY" : "FADING"}</span>
          <TmBadge tm={c.tm} t={c.ticker} />
        </div>
      ))}
      {!rows.length && <p className="fc-num text-cyan-200/60">Scanner warming up…</p>}
    </div>
  </section>
);

const Entries = ({ log, open }) => {
  const ent = (log || []).filter((l) => l.action === "entry" || l.action === "failed");
  return (
    <section className="fc-panel min-h-0 p-[0.6em] flex flex-col" data-testid="v2-entries">
      <p className="fc-label mb-[0.3em]">ENTRIES · {open?.length || 0} open</p>
      <div className="min-h-0 flex-1 overflow-hidden space-y-[0.25em]">
        {ent.slice(0, 12).map((l, i) => (
          <div key={`${l.ticker}-${i}`} className="fc-num text-[1em] border-b border-white/5 pb-[0.15em]" data-testid={`v2-entry-${l.ticker}`}>
            <p className="flex gap-[0.5em]"><span className="text-cyan-200/60">{tm(l.ts)}</span><b className="text-cyan-50">{l.ticker}</b>
              <span className={l.action === "entry" ? "text-[#2CF6B3]" : "text-[#FF3D68]"}>{l.action === "entry" ? `FILLED ${usd(l.filled_usd)}` : "REFUSED"}</span>
              <span className="ml-auto text-cyan-200/70">SL {l.stop} · TP {l.target}</span></p>
            {l.error && <p className="text-[#FF3D68]/80 truncate">{l.error}</p>}
          </div>
        ))}
        {!ent.length && <p className="fc-num text-cyan-200/60">No entries yet — V2 enters from 9:35 ET when a mover is still holding.</p>}
      </div>
    </section>
  );
};

const etDay = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

const Recap = ({ r }) => (
  <section className="fc-panel min-h-0 p-[0.6em] flex flex-col" data-testid="v2-recap">
    <p className="fc-label mb-[0.3em]">4:05 ET RECAP · NET {sgn(r.net)} · realized {sgn(r.realized)} · open {sgn(r.unrealized)}</p>
    <div className="min-h-0 flex-1 overflow-hidden fc-num text-[1em]">
      {r.closed.map((x, i) => (
        <p key={`c${i}`} className="flex gap-[0.5em] border-b border-white/5 py-[0.1em]" data-testid={`v2-recap-closed-${x.symbol}`}>
          <b className="text-cyan-50">{x.symbol}</b><span className="text-cyan-200/70 truncate">{x.entry} → {x.exit} · {x.why}</span><b className={`ml-auto ${tone(x.pnl)}`}>{sgn(x.pnl)}</b></p>
      ))}
      {r.open.map((x, i) => (
        <p key={`o${i}`} className="flex gap-[0.5em] border-b border-white/5 py-[0.1em]" data-testid={`v2-recap-open-${x.symbol}`}>
          <b className="text-cyan-50">{x.symbol}</b><span className="text-amber-200">OPEN</span><span className="text-cyan-200/70">{x.entry} → {x.now}</span><b className={`ml-auto ${tone(x.pnl)}`}>{sgn(x.pnl)}</b></p>
      ))}
      {!r.closed.length && !r.open.length && <p className="text-cyan-200/60">No V2 trades today.</p>}
    </div>
  </section>
);

export const EngineV2Board = ({ d }) => {
  const st = d?.stage || {}, rows = d?.last_scan?.candidates || [];
  return (
    <div className="min-h-0 h-full flex flex-col gap-[0.55em]" data-testid="engine-v2-board">
      <div className="grid grid-cols-5 gap-[0.45em]">
        <Stat label="ENGINE" value={d?.settings?.on ? "LIVE" : "OFF"} cls={d?.settings?.on ? "text-[#2CF6B3]" : "text-[#FF3D68]"} testid="v2-on" />
        <Stat label="PER TRADE" value={usd(3500 * (st.mult || 0.25))} testid="v2-size" />
        <Stat label="WIN RATE" value={st.win_rate == null ? "--" : `${st.win_rate}%`} cls={st.win_rate >= 45 ? "text-[#2CF6B3]" : "text-cyan-50"} testid="v2-win-rate" />
        <Stat label="NET P&L" value={sgn(st.net_usd)} cls={tone(st.net_usd)} testid="v2-net" />
        <Stat label="TRADES · TODAY" value={`${st.closed_trades || 0} · ${d?.entries_today || 0}`} testid="v2-trades" />
      </div>
      <StageBar st={st} />
      <div className="min-h-0 flex-1 grid gap-[0.55em]" style={{ gridTemplateColumns: "minmax(0,58fr) minmax(0,42fr)" }}>
        <Scan rows={rows} />
        {d?.recap?.day === etDay() ? <Recap r={d.recap} /> : <Entries log={d?.log} open={d?.open} />}
      </div>
    </div>
  );
};

export const FloorTvV2 = () => <div className="min-h-0" data-testid="tv-v2-screen"><EngineV2Board d={useEngineV2()} /></div>;
